import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import GeometryCollection from 'ol/geom/GeometryCollection';
import type Geometry from 'ol/geom/Geometry';
import { intersects } from 'ol/extent';
import type { Extent } from 'ol/extent';
import type { LabelMode, LayerModel, OperationalAttributeField, Selection, ViewScope, WorkScope } from '../core/types';
import type { LabelConfig, LabelTargetSource, LabelTemplateTarget } from '../presentation/config';
import { defaultLabelConfig } from '../presentation/config';
import { effectiveFieldCatalog, fieldValueForMasterEntity, formatAttributeTemplate, formatNumericAttribute, SYSTEM_AREA_FIELD_IDS } from './attributes';
import { scopedFragmentIds } from './scope';
import { workScopeFragmentIds } from './scope';
import { geodesicArea } from './measurement';
import { entityViewEntities } from './entityView';

export type LabelLevel = 'finca' | 'suerte' | 'tablon' | 'fragment';
export interface LogicalLabelProperties { labelId: string; level: LabelLevel; text: string; areaHa: number }
export interface LabelContext {
  readonly viewScope?: ViewScope;
  readonly activeFincaId?: string | null;
  readonly labelConfig?: LabelConfig;
  readonly zoom?: number;
  readonly visibleExtent?: Extent;
  readonly operationalFields?: readonly OperationalAttributeField[];
  readonly workScope?: WorkScope;
  readonly allowedFragmentIds?: ReadonlySet<string>;
  readonly labelTargetSource?: LabelTargetSource;
}

const labelAnchorCache = new WeakMap<LayerModel, Map<string, [number, number] | null>>();

const polygonsFrom = (geometry: Geometry | undefined): Polygon[] => {
  if (geometry instanceof Polygon) return [geometry];
  if (geometry instanceof MultiPolygon) return geometry.getPolygons();
  if (geometry instanceof GeometryCollection) return geometry.getGeometries().flatMap(polygonsFrom);
  return [];
};

export function logicalAnchor(geometries: readonly Geometry[]): [number, number] | null {
  const polygons = geometries.flatMap(polygonsFrom);
  if (!polygons.length) {
    const first = geometries[0];
    if (!first) return null;
    const extent = first.getExtent();
    const minX = extent[0]!; const minY = extent[1]!; const maxX = extent[2]!; const maxY = extent[3]!;
    return first.getClosestPoint([(minX + maxX) / 2, (minY + maxY) / 2]) as [number, number];
  }
  const weighted = polygons.map((polygon) => ({ polygon, area: Math.max(polygon.getArea(), 1), point: polygon.getInteriorPoint().getCoordinates() }));
  const total = weighted.reduce((sum, item) => sum + item.area, 0);
  const candidate: [number, number] = [
    weighted.reduce((sum, item) => sum + (item.point[0] ?? 0) * item.area, 0) / total,
    weighted.reduce((sum, item) => sum + (item.point[1] ?? 0) * item.area, 0) / total,
  ];
  if (polygons.some((polygon) => polygon.intersectsCoordinate(candidate))) return candidate;
  return [...weighted].sort((a, b) => b.area - a.area)[0]?.point.slice(0, 2) as [number, number] | undefined ?? null;
}

const cachedLogicalAnchor = (model: LayerModel, id: string, fragmentIds: readonly string[], geometries: readonly Geometry[]): [number, number] | null => {
  let cache = labelAnchorCache.get(model);
  if (!cache) { cache = new Map(); labelAnchorCache.set(model, cache); }
  const key = `${id}\0${fragmentIds.join('\0')}`;
  if (cache.has(key)) return cache.get(key) ?? null;
  const anchor = logicalAnchor(geometries); cache.set(key, anchor); return anchor;
};

const makeLabel = (id: string, level: LabelLevel, text: string, areaHa: number, anchor: [number, number] | null, config: LabelConfig) => {
  const override = config.positionOverrides[id];
  const coordinate = override ? [...override.coordinate] as [number, number] : anchor;
  if (!coordinate) return null;
  return new Feature<Point>({ geometry: new Point(coordinate), labelId: id, level, text, areaHa, manuallyPositioned: Boolean(override) });
};

export function buildLabelFeatures(model: LayerModel, mode: LabelMode, selection: Selection, context: LabelContext = {}): Feature<Point>[] {
  if (mode === 'none') return [];
  const labels: Feature<Point>[] = [];
  const scope = context.viewScope ?? 'all';
  const config = context.labelConfig ?? defaultLabelConfig;
  const numericFormats = { ...config.numericFormats };
  const fields = effectiveFieldCatalog(model, context.operationalFields);
  fields.forEach((field) => {
    const format = config.numericFormats[field.id]; if (format && !numericFormats[field.name]) numericFormats[field.name] = format;
  });
  const allowedFragments = context.allowedFragmentIds ?? (context.workScope
    ? workScopeFragmentIds(model, context.workScope)
    : scopedFragmentIds(model, scope, context.activeFincaId ?? null, selection));
  const scopedSuerteIds = entityViewEntities(model, 'suerte', allowedFragments).map(({ id }) => id);
  const scopedFincaIds = entityViewEntities(model, 'finca', allowedFragments).map(({ id }) => id);
  const scopedTablonIds = entityViewEntities(model, 'tablon', allowedFragments).map(({ id }) => id);
  const activeAreaHa = (fragmentIds: readonly string[]): number => fragmentIds.reduce((sum, id) => {
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    return sum + (geometry ? geodesicArea(geometry) / 10_000 : 0);
  }, 0);
  const inContext = (fragmentIds: readonly string[]): boolean => fragmentIds.some((id) => {
    if (!allowedFragments.has(id)) return false;
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    return geometry ? !context.visibleExtent || intersects(geometry.getExtent(), context.visibleExtent) : false;
  });
  const configuredText = (target: LabelTemplateTarget, attributes: Readonly<Record<string, string | number | boolean | null>>, areaHa: number, logicalAreaFieldId?: string): string => {
    const template = config.templates[target];
    const text = formatAttributeTemplate(template, attributes, numericFormats);
    const areaFormat = (logicalAreaFieldId ? numericFormats[logicalAreaFieldId] : undefined) ?? numericFormats.Area_Ha ?? { decimals: 1 as const, prefix: '', suffix: '', unit: 'ha' };
    const templateContainsArea = [...template.matchAll(/\{([^{}]+)\}/g)].some((match) => match[1]!.trim() === 'Area_Ha' || match[1]!.trim() === logicalAreaFieldId);
    return config.showArea && !templateContainsArea ? `${text}${text ? ' · ' : ''}${formatNumericAttribute(areaHa, areaFormat)}` : text;
  };
  const templateAttributes = (
    target: LabelTemplateTarget,
    fragmentIds: readonly string[],
    ids: { fincaId?: string; suerteId?: string; tablonId?: string; fragmentId?: string },
    overrides: Readonly<Record<string, string | number | boolean | null>> = {},
  ): Readonly<Record<string, string | number | boolean | null>> => {
    const result: Record<string, string | number | boolean | null> = { ...overrides };
    [...config.templates[target].matchAll(/\{([^{}]+)\}/g)].forEach((match) => {
      const field = match[1]?.trim(); if (!field || field in result) return;
      const value = fieldValueForMasterEntity(model, field, fragmentIds, ids, context.operationalFields);
      if (value !== undefined) result[field] = value;
    });
    return result;
  };
  const addSuerte = (suerteId: string, allowExplicit = false, target: LabelTemplateTarget = 'suerte'): void => {
    const suerte = model.suertes.get(suerteId);
    if (!suerte || (!allowExplicit && suerte.code.startsWith('__sin_suerte_')) || (!allowExplicit && !inContext(suerte.fragmentIds))) return;
      const fragmentIds = suerte.fragmentIds.filter((id) => allowedFragments.has(id));
      if (!fragmentIds.length) return;
      const geometries = fragmentIds.flatMap((id) => {
        const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
        return geometry ? [geometry] : [];
      });
      const areaHa = activeAreaHa(suerte.fragmentIds);
      const ids = { fincaId: suerte.fincaId, suerteId: suerte.id };
      const attributes = templateAttributes(target, fragmentIds, ids, { Suerte: suerte.label, CodSuerte: suerte.code });
      const label = makeLabel(suerte.id, 'suerte', configuredText(target, attributes, areaHa, SYSTEM_AREA_FIELD_IDS.suerteHa), areaHa, cachedLogicalAnchor(model, suerte.id, fragmentIds, geometries), config);
      if (label) labels.push(label);
  };
  const addFinca = (fincaId: string, allowExplicit = false, target: LabelTemplateTarget = 'finca'): void => {
    const finca = model.fincas.get(fincaId);
    if (!finca || (!allowExplicit && !inContext(finca.fragmentIds))) return;
    const fragmentIds = finca.fragmentIds.filter((id) => allowedFragments.has(id));
    if (!fragmentIds.length) return;
    const geometries = fragmentIds.flatMap((id) => { const geometry = model.fragmentsById.get(id)?.feature.getGeometry(); return geometry ? [geometry] : []; });
    const areaHa = activeAreaHa(finca.fragmentIds);
    const ids = { fincaId: finca.id };
    const attributes = templateAttributes(target, fragmentIds, ids, { Nombre: finca.name, Finca: finca.name, Codfinca: finca.code });
    const label = makeLabel(finca.id, 'finca', configuredText(target, attributes, areaHa, SYSTEM_AREA_FIELD_IDS.fincaHa), areaHa, cachedLogicalAnchor(model, finca.id, fragmentIds, geometries), config);
    if (label) labels.push(label);
  };
  const addTablon = (tablonId: string, allowExplicit = false, target: LabelTemplateTarget = 'tablon'): void => {
    const tablon = model.tablones.get(tablonId);
    if (!tablon || tablon.code === '__sin_tablon__' || (!allowExplicit && !inContext(tablon.fragmentIds))) return;
      const fragmentIds = tablon.fragmentIds.filter((id) => allowedFragments.has(id));
      if (!fragmentIds.length) return;
      const geometries = fragmentIds.flatMap((id) => {
        const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
        return geometry ? [geometry] : [];
      });
      const suerte = model.suertes.get(tablon.suerteId);
      const areaHa = activeAreaHa(tablon.fragmentIds);
      const ids = { fincaId: suerte?.fincaId, suerteId: tablon.suerteId, tablonId: tablon.id };
      const attributes = templateAttributes(target, fragmentIds, ids, { Tablon: tablon.code, Suerte: suerte?.label ?? '', CodSuerte: suerte?.code ?? '' });
      const label = makeLabel(tablon.id, 'tablon', configuredText(target, attributes, areaHa, SYSTEM_AREA_FIELD_IDS.tablonHa), areaHa, cachedLogicalAnchor(model, tablon.id, fragmentIds, geometries), config);
      if (label) labels.push(label);
  };
  const addFragment = (fragmentId: string, allowExplicit = false, target: LabelTemplateTarget = 'fragment'): void => {
    const fragment = model.fragmentsById.get(fragmentId); const geometry = fragment?.feature.getGeometry();
    if (!fragment || !geometry || (!allowExplicit && !inContext([fragment.id]))) return;
    const areaHa = geodesicArea(geometry) / 10_000;
    const ids = { fincaId: fragment.fincaId, suerteId: fragment.suerteId, tablonId: fragment.tablonId, fragmentId: fragment.id };
    const attributes = templateAttributes(target, [fragment.id], ids, fragment.attributes);
    const label = makeLabel(fragment.id, 'fragment', configuredText(target, attributes, areaHa, SYSTEM_AREA_FIELD_IDS.fragmentHa), areaHa, cachedLogicalAnchor(model, fragment.id, [fragment.id], [geometry]), config);
    if (label) labels.push(label);
  };
  const hasExplicitScope = Boolean(context.workScope?.fincaIds.length);
  const focusedTarget = Boolean(context.labelTargetSource && context.labelTargetSource !== 'scope' && context.labelTargetSource !== 'none');
  const generalLabelsAllowed = focusedTarget || hasExplicitScope || scope !== 'all' || (context.zoom ?? Number.POSITIVE_INFINITY) >= 12;
  const tablonLabelsAllowed = focusedTarget || Boolean(context.workScope?.tablonIds.length) || scope === 'selection' || (context.zoom ?? Number.POSITIVE_INFINITY) >= 14;
  if (generalLabelsAllowed && mode === 'fincas') scopedFincaIds.forEach((id) => addFinca(id));
  if (generalLabelsAllowed && (mode === 'suerte' || mode === 'suerte_tablones')) scopedSuerteIds.forEach((id) => addSuerte(id));
  if (generalLabelsAllowed && tablonLabelsAllowed && (mode === 'tablones' || mode === 'suerte_tablones')) scopedTablonIds.forEach((id) => addTablon(id));
  if (generalLabelsAllowed && tablonLabelsAllowed && mode === 'fragmentos') allowedFragments.forEach((id) => addFragment(id));
  if (mode === 'seleccion' && selection) {
    if (selection.kind === 'suerte') addSuerte(selection.id, true, 'selection');
    if (selection.kind === 'tablon') addTablon(selection.id, true, 'selection');
    if (selection.kind === 'finca') {
      const finca = model.fincas.get(selection.id);
      if (finca) {
        const fragmentIds = finca.fragmentIds.filter((id) => allowedFragments.has(id));
        const geometries = fragmentIds.flatMap((id) => {
          const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
          return geometry ? [geometry] : [];
        });
        const areaHa = activeAreaHa(finca.fragmentIds);
        const attributes = templateAttributes('selection', fragmentIds, { fincaId: finca.id }, { Nombre: finca.name, Finca: finca.name, Codfinca: finca.code });
        const label = makeLabel(finca.id, 'finca', configuredText('selection', attributes, areaHa), areaHa, cachedLogicalAnchor(model, finca.id, fragmentIds, geometries), config);
        if (label) labels.push(label);
      }
    }
    if (selection.kind === 'fragment') {
      const fragment = model.fragmentsById.get(selection.id);
      const geometry = fragment?.feature.getGeometry();
      if (fragment && geometry) {
        const areaHa = geodesicArea(geometry) / 10_000;
        const ids = { fincaId: fragment.fincaId, suerteId: fragment.suerteId, tablonId: fragment.tablonId, fragmentId: fragment.id };
        const attributes = templateAttributes('selection', [fragment.id], ids, fragment.attributes);
        const label = makeLabel(fragment.id, 'fragment', configuredText('selection', attributes, areaHa), areaHa, cachedLogicalAnchor(model, fragment.id, [fragment.id], [geometry]), config);
        if (label) labels.push(label);
      }
    }
  }
  return labels;
}
